import { environment } from '../environments/environment'
import axios from 'axios'
import moment from 'moment'

const apiEndpoint = environment.webservices.endpoint
const JSON_HEADERS = { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }

// Dedupe concurrent getPatientInfo requests for the same patient.
// Any second caller that fires while the first is still in flight gets the same promise
// back instead of triggering a duplicate HTTP request.
const inFlightPatientInfo: { [key: string]: Promise<any> | undefined } = {}

export class PatientService {
  // constructor() { }

  getPatients(limit: number = 20, page: number = 1) {
    const endpoint = `${apiEndpoint}/patients?limit=${limit}&page=${page}`
    return axios.get(endpoint)
  }
  // ── Patient portal access — the invite ceremony (backend: atman/portal/invites.py).
  // Status/mint/revoke are workforce-domain routes; the patient-side redeem is
  // the portal app's concern, never this one's.
  getPortalStatus(patientRefId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/portal-status`
    return axios.get(endpoint)
  }
  createPortalInvite(patientRefId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/portal-invites`
    return axios.post(endpoint, {})
  }
  revokePortalIdentity(patientRefId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/portal-identity/revoke`
    return axios.post(endpoint, {})
  }
  assignTask() {
    const endpoint = `${apiEndpoint}/daily-assignment`
    return axios.get(endpoint)
  }
  importSFTasks(payload: any, patientRefId: string | null = null) {
    let endpoint = `${apiEndpoint}/import-sf-tasks`
    if (patientRefId && patientRefId.length > 0) {
      endpoint = `${endpoint}?patient_ref_id=${patientRefId}`
    }
    return axios.post(endpoint, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
  getTaskAttempts(taskId: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}/attempts`
    return axios.get(endpoint)
  }
  getDelayTrail(taskId: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}/delay-trail`
    return axios.get(endpoint)
  }
  postTaskLogAttempts(taskId: any, payload: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}/attempts`
    return axios.post(endpoint, payload)
  }
  postTaskDelay(taskId: any, payload: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}/delay`
    return axios.post(endpoint, payload)
  }
  putTaskSchedule(taskId: any, payload: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}`
    return axios.put(endpoint, payload)
  }
  getKBVersion() {
    return axios.get(`${apiEndpoint}/check-kb-version`)
  }
  getPatientsWithFilters(filters: any, isAIDA: boolean) {
    let endpoint = isAIDA
      ? `${apiEndpoint}/aida-patients?limit=${filters.limit}&page=${filters.page}&programs=${filters.programs}&hospitals=${filters?.hospitals}&search=${filters.search}&state=${filters.state}&stage=${filters.stage}&sort=${filters.sort}`
      : `${apiEndpoint}/patients?limit=${filters.limit}&page=${filters.page}&programs=${filters.programs}&hospitals=${filters?.hospitals}&search=${filters.search}&state=${filters.state}&stage=${filters.stage}&sort=${filters.sort}`

    if (filters.activity_days !== '') endpoint = `${endpoint}&activity_days=${filters.activity_days}`

    if (filters.inactive === true || filters.inactive === 'true') endpoint = `${endpoint}&status=inactive`

    if (filters.approval_status === true || filters.approval_status === 'true') endpoint = `${endpoint}&needs_approval=true`

    if (filters?.status) endpoint = `${endpoint}&test_status=${filters.status.toLowerCase()}`

    if (filters.clinic_visit_status) endpoint = `${endpoint}&clinic_visit_status=${filters.clinic_visit_status}`

    if (filters.clinic_visit_days !== '') endpoint = `${endpoint}&clinic_visit_days=${filters.clinic_visit_days?.replace('today', 0)}`

    if (filters.cardiologist) endpoint = `${endpoint}&cardiologists=${filters.cardiologist}`

    return axios.get(endpoint)
  }

  // getFilterPatients(filterParam: any, limit: number = 20, page: number = 1) {
  //   let endpoint = ''
  //   if (filterParam === 'all') {
  //     endpoint = `${apiEndpoint}/patients?limit=${limit}&page=${page}`
  //   } else {
  //     endpoint = `${apiEndpoint}/patients?stage=${filterParam}&limit=${limit}&page=${page}`
  //   }
  //   return axios.get(endpoint)
  // }

  getPatientInfo(patientId: string, selectedPrograms: string[] = []): Promise<any> {
    const programs = selectedPrograms || []
    const key = `${patientId}|${programs.join(',')}`
    const cached = inFlightPatientInfo[key]
    if (cached) {
      return cached
    }
    let endpoint = ''
    if (programs.length) {
      endpoint = `${apiEndpoint}/patients/${patientId}?${programs.map((e, i) => (i === 0 ? `program=${e}` : `&program=${e}`)).join('')}`
    } else {
      endpoint = `${apiEndpoint}/patients/${patientId}`
    }
    const promise = axios.get(endpoint).finally(() => {
      delete inFlightPatientInfo[key]
    })
    inFlightPatientInfo[key] = promise
    return promise
  }

  syncEHRPatients() {
    const endpoint = `${apiEndpoint}/fhir/patients/sync`
    return axios.get(endpoint)
  }
  syncEPICPatients(mrn: any, prg_list: any) {
    const endpoint = `${apiEndpoint}/fhir/patients/${mrn}/sync?programs_name=${prg_list}`
    return axios.get(endpoint)
  }
  syncELATIONPatients(mrn: any, prg_list: any, elationSource?: string) {
    let endpoint = `${apiEndpoint}/elation/patients/${mrn}/sync?programs_name=${prg_list}`
    if (elationSource) {
      endpoint += `&elation_source=${elationSource}`
    }
    return axios.get(endpoint)
  }
  syncELATIONPatientMedications(mrn: any) {
    const endpoint = `${apiEndpoint}/elation/patients/${mrn}/medication_sync`
    return axios.get(endpoint)
  }
  singsCleanup(pId: any) {
    const endpoint = `${apiEndpoint}/elation/${pId}/medications_cleanup/`
    return axios.get(endpoint)
  }
  syncEHRPatientInfo(patientRefId: string) {
    const endpoint = `${apiEndpoint}/fhir/patients/${patientRefId}/sync`
    return axios.get(endpoint)
  }
  createCase(patientId: string, programName: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/salesforce/cases/${programName}`
    return axios.get(endpoint, {})
  }
  getSFCaseStatus(patientId: string, programName: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/${programName}/sync-sf-cases-status`
    return axios.get(endpoint, {})
  }
  getTreatmentPlan(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/treatment-plan`
    return axios.get(endpoint, {})
  }
  getPrescriptionUnit(searchPattern: string) {
    const endpoint = `${apiEndpoint}/prescription-units?name=${searchPattern}`
    return axios.get(endpoint, {})
  }

  putProgramConditionsQuestionnaire(patientId: string, entityRefId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/condition-evaluation/${entityRefId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }

  PostConfirmations(payload: any) {
    const endpoint = `${apiEndpoint}/confirmations`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  putProgramMeasurements(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/measurements/program_targets`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getMeasurementRationale(program: any, name: any) {
    const endpoint = `${apiEndpoint}/get_rationale_dependencies/${name}/${program}`
    return axios.get(endpoint)
  }
  getPSCRationale(program: any, psc: any) {
    const endpoint = `${apiEndpoint}/get_psc_rationale_dependencies/${psc}/${program}`
    return axios.get(endpoint)
  }
  getCalculatorsValue(patientId: string, calId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/calculators/${calId}`
    return axios.put(endpoint, {}, JSON_HEADERS)
  }
  resetDiagnosisStage(patientId: string, planId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/diagnosis_plan/${planId}/reset`
    return axios.put(endpoint, {}, JSON_HEADERS)
  }
  postDiagnosisPlan(patientId: string, problem_id: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/update_diagnosis_plan/${problem_id}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  postConditionsEval(patientId: string, problem_id: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/update_problem_condition/${problem_id}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  autoSelectConditions(patientId: string, enrollmentId: number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/evaluation-differential/auto-select?enrollment_id=${enrollmentId}`
    return axios.put(endpoint, {}, JSON_HEADERS)
  }
  postOtherMedication(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/medications`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }

  async getMetaData() {
    const axiosEndpoint = `${apiEndpoint}/resources/picklist-values`
    const cacheKey = 'picklistValues'
    const envCacheKey = 'picklistValues_env'

    const currentEnv = environment.okta.isEnabled ? 'okta' : environment.oidc.isEnabled ? 'oidc' : environment.aida.isEnabled ? 'aida' : 'none'

    // Get cached data and cached environment
    const cachedData = sessionStorage.getItem(cacheKey)
    const cachedEnv = sessionStorage.getItem(envCacheKey)

    // Check if cache is valid (data exists AND environment matches)
    if (cachedData && cachedEnv === currentEnv) {
      const parsed = JSON.parse(cachedData)
      const fakeResponse: any = {
        data: parsed,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {},
      }
      return Promise.resolve(fakeResponse)
    }

    return axios.get(axiosEndpoint).then((response) => {
      sessionStorage.setItem(cacheKey, JSON.stringify(response.data))
      sessionStorage.setItem(envCacheKey, currentEnv)
      return response
    })
  }
  async getClinicVisitMetaData() {
    const axiosEndpoint = `${apiEndpoint}/resources/picklist-values/aida`
    return axios.get(axiosEndpoint).then((response) => {
      return response
    })
  }
  postMedicationReaction(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/med-intolerances`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  getMedicationReaction() {
    const endpoint = `${apiEndpoint}/medication-reactions`
    return axios.get(endpoint, {})
  }
  putMedicationReaction(patientId: string, entityId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/med-intolerances/${entityId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }

  getOtherConditions(searchPattern: string) {
    const endpoint = `${apiEndpoint}/indications?name=${searchPattern}`
    return axios.get(endpoint, {})
  }

  postOtherConditions(payload: any, patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/conditions`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  deleteOtherMedications(patientId: string, id: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/delete_toxicity/${id}`
    return axios.delete(endpoint, {})
  }
  deleteMedIntolerances(patientId: string, id: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/med-intolerances/${id}`
    return axios.delete(endpoint, {})
  }
  deleteOtherConditions(patientId: string, id: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/conditions/${id}`
    return axios.delete(endpoint, {})
  }
  putOtherMedication(patientId: string, entityId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/medications/${entityId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  editOtherConditions(payload: any, patientId: string, quesId: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/conditions/${quesId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getUpdatedStage(patientId: string, _enrollments: number[]) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/enrollments/stages`
    return axios.put(endpoint, { enrollments: _enrollments }, JSON_HEADERS)
  }
  getClinicalScore(patientId: string, enrollId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/enrollments/${enrollId}`
    return axios.get(endpoint, {})
  }
  postProgramChecks(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/measurements/program_checks`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  postOthers(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/measurements/others`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getRxNorms(name: string, propagation: string, aa_formulation: boolean, medication_class: boolean, ingredient: boolean) {
    const endpoint = `${apiEndpoint}/rxnorms?name=${name}&propagation=${propagation}&medication=${aa_formulation}&class=${medication_class}&ingredient=${ingredient}`
    return axios.get(endpoint, {})
  }
  postExpressMode(payload: any) {
    const endpoint = `${apiEndpoint}/confirmations/express-mode`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  deepLink(patientId: string, enrollId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/enrollments/${enrollId}/salesforce-deeplink`
    return axios.get(endpoint, {})
  }
  captureBaseline(enrollId: string) {
    const endpoint = `${apiEndpoint}/patients/${enrollId}/baseline-data`
    return axios.post(endpoint, {}, JSON_HEADERS)
  }
  postReferral(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/referrals`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getReferral(patientId: string, entityRef: string, refId: number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/referrals/${refId}`
    return axios.get(endpoint, {})
  }
  getOverrideNote(patientId: string, entityRef: string, eId: number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/latest-override-notes?entity_ref=${entityRef}&entity_id=${eId}`
    return axios.get(endpoint, {})
  }
  removeOverride(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/remove-override`
    return axios.put(endpoint, payload)
  }
  postOverrideValue(payload: any) {
    const endpoint = `${apiEndpoint}/manual-override`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getFilterCounts(isAIDA = false, program: string, hospitals: string, status: string) {
    let endpoint = isAIDA ? `${apiEndpoint}/filter-data-counts/aida` : `${apiEndpoint}/filter-data-counts`

    endpoint = `${endpoint}?programs=${program}&hospitals=${hospitals}&status=${status}`
    return axios.get(endpoint, {})
  }
  getIcdCode(searchPattern: string) {
    const endpoint = `${apiEndpoint}/icds?search=${searchPattern}`
    return axios.get(endpoint, {})
  }
  getPscDetails(patientId: string, entityRefId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/condition-evaluation/${entityRefId}`
    return axios.get(endpoint, {})
  }
  tokenVerification() {
    const endpoint = `${apiEndpoint}/auth/token-verification`
    return axios.post(endpoint)
  }
  putMedSubActions(patientId: string, program_name: string, sub_action: string, medId: string, cpId: any, sub_id: string, notes: any) {
    let endpoint = `${apiEndpoint}/patients/${patientId}/${program_name}/med-actions?action=substitute&sub_action=${sub_action}`
    return axios.put(endpoint, {
      medication_id: medId,
      care_plan_id: cpId,
      sub_id: sub_id,
      notes: notes,
    }, JSON_HEADERS)
  }
  getMedSubDetails(patientId: string, sub_id: string, med_id: string) {
    let endpoint = `${apiEndpoint}/patients/${patientId}/get_substitution_details/${sub_id}/${med_id}`
    return axios.get(endpoint)
  }
  putPatientActions(patientId: string, id: string, program_name: string, action: string, sub_action: string, _care_plan_id: any) {
    let endpoint = `${apiEndpoint}/patients/${patientId}/${program_name}/med-actions?action=${encodeURI(action)}`
    if (sub_action !== '') endpoint += `&sub_action=${sub_action}`
    return axios.put(endpoint, {
      medication_id: id,
      care_plan_id: _care_plan_id,
    }, JSON_HEADERS)
  }
  getMedClasses(carePlanId: number, limit: number = 20, page: number = 1) {
    const endpoint = `${apiEndpoint}/med-classes?care-planId=${carePlanId}&limit=${limit}&page=${page}`
    return axios.get(endpoint)
  }
  getMedications(classId: any, limit: number = 20, page: number = 1) {
    const endpoint = `${apiEndpoint}/aa_formulations/${classId}`
    return axios.get(endpoint)
  }
  postProviderMedications(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/medications?group=provider_recommended`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  getMedFrequencyByUnit(data: any) {
    const endpoint = `${apiEndpoint}/resources/frequency_list/${data}`
    return axios.get(endpoint)
  }
  getMeasurementReport(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/measurement-report-data`
    return axios.get(endpoint)
  }
  getMeasurementsByTest(test_name: string, programs: string, enrollmentId?: string | number) {
    let endpoint = `${apiEndpoint}/tests/measurements-per-test/${test_name}?programs=${programs}`
    if (enrollmentId) {
      endpoint += `&enrollment_id=${enrollmentId}`
    }
    return axios.get(endpoint)
  }
  getSchedules(aa_id: any) {
    const endpoint = `${apiEndpoint}/aa_formulation/schedules/${aa_id}`
    return axios.get(endpoint)
  }
  getOverlapSchedule(patientId: string, medId: any, scheduleName: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/check_overlap_schedule/${medId}/${scheduleName}`
    return axios.get(endpoint)
  }
  getAAFormContextualHelp(id: any) {
    const endpoint = `${apiEndpoint}/disease-education/aa_formulations/${id}`
    return axios.get(endpoint)
  }
  getContextualHelp(category: string, id: any) {
    const endpoint = `${apiEndpoint}/disease-education/${category}/${id}`
    return axios.get(endpoint)
  }
  getMedChoicesData(patientId: string, medId: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/med-choices/${medId}`
    return axios.get(endpoint)
  }
  getMedSubList(patientId: string, medId: any, prg: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/get_substitution/${medId}/${prg}`
    return axios.get(endpoint)
  }
  getMedRestrictions(patientId: string, prg: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/check_exclusions/${prg}`
    return axios.get(endpoint)
  }
  getRestrictedMed(patientId: string, recordId: string, medType: string, type: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/restricted-aa-forms?id=${recordId}&type=${medType}&propagation=${type}`
    return axios.get(endpoint)
  }
  getMedMoreDetails(patientId: string, recordId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/medications/${recordId}/fhir-json`
    return axios.get(endpoint)
  }
  deleteMedication(patientId: string, recordId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/delete_medication/${recordId}`
    return axios.delete(endpoint)
  }
  getImportedMedMoreDetails(patientId: string, recordId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/imported-medications/${recordId}/fhir-json`
    return axios.get(endpoint)
  }
  postNewPatient(payload: any) {
    const endpoint = `${apiEndpoint}/ccd/uploads`
    return axios.post(endpoint, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
  postCCDFiles(mrn: any, payload: any) {
    const endpoint = `${apiEndpoint}/elation/${mrn}/medications_cleanup_ccd`
    return axios.post(endpoint, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
  generateTxtReport(hospital: any, payload: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital}/generate-zoom-txt-interaction-report`
    return axios.post(endpoint, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'arraybuffer',
    })
  }
  extractTxtReport(hospital: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital}/exports/zoom-txt-interaction-report`
    return axios.get(endpoint, { responseType: 'blob' })
  }
  createDashboardSheet(payload: any) {
    const endpoint = `${apiEndpoint}/create_dashboard_sheet`
    return axios.post(endpoint, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
  addPatientsFiles(payload: any, hospitalTag: string = 'optum') {
    const endpoint = `${apiEndpoint}/elation/add_new_patients_on_elation`
    // Use .set() (not .append()) so our value always wins over any stale
    // hospital_tag entry upstream. HIPAA: misrouting here would tag a
    // HealthyHeart patient as Optum-only in Elation.
    if (payload && typeof payload.set === 'function') {
      payload.set('hospital_tag', hospitalTag)
    }
    return axios.post(endpoint, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
  addAppointmentsFiles(payload: any, hospitalTag: string = 'optum') {
    const endpoint = `${apiEndpoint}/elation/create_new_appointments_on_elation`
    // Use .set() to guarantee the correct hospital_tag is sent (see above).
    if (payload && typeof payload.set === 'function') {
      payload.set('hospital_tag', hospitalTag)
    }
    return axios.post(endpoint, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
  changeProgramStatus(patientId: string, program_name: string, status: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/${program_name}/${status}`
    return axios.put(endpoint)
  }
  runToxicities(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/toxicities`
    return axios.post(endpoint, payload)
  }
  changeProgramStage(patientId: string, program_name: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/${program_name}/change-stage`
    return axios.put(endpoint, {}, JSON_HEADERS)
  }
  getPatientProfileDetails(patientId: string) {
    const endpoint = `${apiEndpoint}/patient-profile/${patientId}`
    return axios.get(endpoint)
  }
  getProviders(patientId: any) {
    const endpoint = `${apiEndpoint}/patient/${patientId}/providers`
    return axios.get(endpoint)
  }
  putClinicalDetails(patientId: any, payload: any) {
    const endpoint = `${apiEndpoint}/patient/${patientId}/providers`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  postClinicVisit(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/clinic-visits`
    return axios.post(endpoint, payload)
  }
  putClinicVisit(patientId: string, visitId: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/clinic-visits/${visitId}`
    return axios.put(endpoint, payload)
  }
  putPatientProfileDetails(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patient-profile/${patientId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getMedHistory(medId: string) {
    const endpoint = `${apiEndpoint}/medications/${medId}/history`
    return axios.get(endpoint)
  }
  getMeasurementHistory(patientId: string, mesureId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/measurements/${mesureId}/history`
    return axios.get(endpoint)
  }
  getTargetHistory(patientId: string, program: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/target-history?program=${program}`
    return axios.get(endpoint)
  }
  resetPatient(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/reset`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  regenerateCarePlan(patientId: string, carePlanId: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/care_plans/${carePlanId}/regenerate`
    return axios.get(endpoint)
  }

  resetCarePlan(patientId: string, carePlanId: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/care_plans/${carePlanId}/reset`
    return axios.get(endpoint)
  }

  deviceActivation(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/withings/device-activation`
    return axios.post(endpoint)
  }
  syncDeviceData(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/withings/device-sync`
    return axios.post(endpoint)
  }
  syncMeasurementData(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/device-data-sync/weight`
    return axios.post(endpoint)
  }
  journeyByProgram(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/journey`
    return axios.get(endpoint)
  }
  measurementsUpdate(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/measurements/update`
    return axios.put(endpoint, payload)
  }
  deleteTargetHistory(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/target-histories/removal`
    return axios.post(endpoint, payload)
  }
  deleteMeasurementsHistory(patientId: string, mId: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/measurements/${mId}`
    return axios.delete(endpoint)
  }
  extractHospitalRXReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/rx-report`
    return axios.get(endpoint)
  }
  extractHospitalBPReport() {
    const endpoint = `${apiEndpoint}/exports/bp-report`
    return axios.get(endpoint, { responseType: 'arraybuffer' })
  }
  extractHospitalInteractionReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/interaction-report`
    return axios.get(endpoint)
  }
  extractHospitalNotesReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/notes`
    return axios.get(endpoint)
  }
  extractHospitalLabReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/lab-report`
    return axios.get(endpoint)
  }
  extractMigrationReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/migration-report`
    return axios.get(endpoint, { responseType: 'arraybuffer' })
  }
  fhirPatientAdd(mrn: string, programs: string) {
    const endpoint = `${apiEndpoint}/fhir/patients/${mrn}/sync?programs_name=${programs}`
    return axios.get(endpoint)
  }
  elationPatientAdd(mrn: string, programs: string, elationSource?: string) {
    let endpoint = `${apiEndpoint}/elation/patients/${mrn}/sync?programs_name=${programs}`
    if (elationSource) {
      endpoint += `&elation_source=${elationSource}`
    }
    return axios.get(endpoint)
  }
  syncToElationPatient(elation_id: string) {
    const endpoint = `${apiEndpoint}/elation/patients/${elation_id}/medication_sync_back_to_elation`
    return axios.get(endpoint)
  }
  syncMedToElationPatient(elation_id: string, med_id: string) {
    const endpoint = `${apiEndpoint}/elation/patients/${elation_id}/medication_sync_back_to_elation/${med_id}`
    return axios.get(endpoint)
  }
  getSigsConversion(payload: string) {
    const endpoint = `${apiEndpoint}/get_direction_frequency_conversion`
    return axios.post(endpoint, payload)
  }
  addManualPatient(payload: any) {
    const endpoint = `${apiEndpoint}/patients`
    return axios.post(endpoint, payload, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } })
  }
  exportPatient(patientId: string) {
    return axios({
      url: `${apiEndpoint}/patients/${patientId}/export`,
      method: 'GET',
      responseType: 'blob', // to download file
    })
  }
  exportPatients(hospitals: string[]) {
    const endpoint = `${apiEndpoint}/patients/bulk_hospital_export`
    return axios.post(endpoint, { hospital_names: hospitals }, { responseType: 'blob' })
  }
  elationimportStatus(hospitalTag: string = 'optum') {
    const endpoint = `${apiEndpoint}/elation/patients/check-import-status?hospital_tag=${hospitalTag}`
    return axios.get(endpoint)
  }
  getElationImport(elationSource: string = 'atman', hospitalTag: string = 'optum') {
    const endpoint = `${apiEndpoint}/elation/patients/?elation_source=${elationSource}&hospital_tag=${hospitalTag}`
    return axios.post(endpoint)
  }
  extractSpreadsheet() {
    const endpoint = `${apiEndpoint}/exports/dashboard-spreadsheet`
    return axios.get(endpoint, { responseType: 'blob' })
  }
  extractTreatmentReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/treatment-goal-report`
    return axios.get(endpoint)
  }
  extractAIDAReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/interaction-report`
    return axios.get(endpoint)
  }
  generateExports(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/generate-exports`
    return axios.post(endpoint)
  }
  generateDashboardReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/dashboard-report`
    return axios.post(endpoint)
  }
  getDashboardReportStatus(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/dashboard-report/status`
    return axios.get(endpoint)
  }
  extractDashboardReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/exports/dashboard-report`
    return axios.get(endpoint)
  }
  downloadTasksEventsExport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/reports/task-events`
    return axios.get(endpoint, { responseType: 'blob' })
  }
  generateTasksEventsReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/reports/task-events/generate`
    return axios.post(endpoint)
  }
  downloadCaseRateFunnelReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/reports/case-rate-funnel`
    return axios.get(endpoint, { responseType: 'blob' })
  }
  downloadInactiveCandidatesReport(hospital_name: any) {
    const endpoint = `${apiEndpoint}/hospitals/${hospital_name}/reports/inactive-patient-candidates`
    return axios.get(endpoint, { responseType: 'blob' })
  }
  downloadeExportsReport() {
    const endpoint = `${apiEndpoint}/elation/patients/import-status`
    return axios.get(endpoint, { responseType: 'blob' })
  }
  downloadeElationReport() {
    return axios({
      url: `${apiEndpoint}/elation/patients/import-status`,
      method: 'GET',
      responseType: 'blob',
    })
  }
  generateNote(patientId: string, includeOnboardingHeader: boolean = false, interweaveScribe: boolean = true) {
    const params = new URLSearchParams()
    if (includeOnboardingHeader) params.set('include_onboarding_header', 'true')
    // Backend defaults interweave_scribe=true (narrative note); only send the param when
    // the provider opts out to the simpler structured note.
    if (!interweaveScribe) params.set('interweave_scribe', 'false')
    const qs = params.toString()
    return axios.get(`${apiEndpoint}/elation/patients/${patientId}/generate_note${qs ? `?${qs}` : ''}`)
  }
  closeoutNote(patientId: string, payload: any) {
    return axios.post(`${apiEndpoint}/elation/patients/${patientId}/generate_closeout_note`, payload)
  }
  submitNote(patientId: string, payload: any) {
    return axios.post(`${apiEndpoint}/elation/patients/${patientId}/submit_note`, payload)
  }

  /**
   * Sync ICD-10 codes to Elation as problems
   */
  syncICD10ToElation(patientId: string) {
    return axios.post(`${apiEndpoint}/elation/patients/${patientId}/sync-icd10`)
  }

  /**
   * Sync medication restrictions/intolerances to Elation as drug intolerances
   */
  syncIntolerancesToElation(patientId: string) {
    return axios.post(`${apiEndpoint}/elation/patients/${patientId}/sync-intolerances`)
  }
  /**
   * Sync a site to Elation as a medical_group contact and add to patient's care team
   */
  syncSiteToElation(siteId: string | number, patientId?: string | number) {
    return axios.post(`${apiEndpoint}/elation/sites/${siteId}/sync`, patientId ? { patient_id: patientId } : {})
  }
  importPatient(payload: any) {
    const endpoint = `${apiEndpoint}/patients/import`
    return axios.post(endpoint, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }
  runProgramEvaluation(id: string) {
    const endpoint = `${apiEndpoint}/program_evaluation/${id}`
    return axios.get(endpoint, {})
  }

  /**
   * Run program evaluation for all medications in a care plan at once
   * @param carePlanId - The care plan ID
   */
  runProgramEvaluationBulk(carePlanId: number) {
    const endpoint = `${apiEndpoint}/program_evaluation/care_plan/${carePlanId}`
    return axios.get(endpoint)
  }

  /**
   * Approve all medication evaluations for a program at once
   * @param patientId - Patient ID
   * @param programName - Program name
   * @param payload - Contains care_plan_id, medication_ids, and action
   */
  approveAllMedicationEvaluations(
    patientId: string,
    programName: string,
    payload: {
      care_plan_id: number
      medication_ids: number[]
      action: 'approve' | 'reject'
      next_action?: 'ext_int' | 'continue_tox_gate' | 'skip_sch_tox'
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/${programName}/med-actions/bulk`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }

  getCalculatedPSC(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/get_program_specific_conditions`
    return axios.get(endpoint, {})
  }
  recalculateICD10(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/icd-codes`
    return axios.get(endpoint, {})
  }
  syncDeviceMeasurements() {
    const endpoint = `${apiEndpoint}/patients/withings/device-sync`
    return axios.post(endpoint, {})
  }
  getMedicationReactionBySearch(name: string) {
    const endpoint = `${apiEndpoint}/symptoms?name=${name}`
    return axios.get(endpoint, {})
  }
  getMedEvalRejectDDL(program_name: string, eval_status: string, med_name: string) {
    const endpoint = `${apiEndpoint}/provider_actions/${program_name}/${eval_status}/${med_name}`
    return axios.get(endpoint)
  }
  searchDiagnoses(name: string) {
    // Diagnosis picker (other-conditions). Reactions do NOT search here —
    // they ride the symptom namespace via searchSymptomConcepts (F178).
    const endpoint = `${apiEndpoint}/diagnoses?name=${encodeURIComponent(name)}`
    return axios.get(endpoint, {})
  }
  getQuestionnaires(limit: number = 25, page: number = 1, evalUnion: boolean = false) {
    // evalUnion=true → the eval-union catalog (all evaluation programs' dependent
    // questionnaires) for the Search dialog. See PROGRAM_DATA_SURFACING_RULES.md §0 R3.
    let endpoint = `${apiEndpoint}/questionnaires`
    if (evalUnion) endpoint += `?eval_union=true`
    return axios.get(endpoint)
  }
  getQuestionnaireDataset(questionnaireId: string, patientId: string, signal?: AbortSignal, presetIntent?: 'confirm' | 'deny') {
    // presetIntent: when the inline editor opens for a chip whose intent isn't saved yet,
    // the backend returns the resolved DEFAULT answer set (preview, not persisted) so the
    // editor shows propagated values immediately. See PROVIDER_QUESTIONNAIRE_UX_SPEC.md.
    const qs = presetIntent ? `?preset_intent=${presetIntent}` : ''
    const endpoint = `${apiEndpoint}/patients/${patientId}/questionnaires/${questionnaireId}${qs}`
    const resultSet = axios
      .get(endpoint, { signal })
      .then((response: any) => {
        return response !== undefined && response?.data && response?.data?.questionnaire ? response?.data?.questionnaire : {}
      })
      .catch((error: any) => {
        if (axios.isCancel(error)) return undefined
        console.error(error)
        return {}
      })
    return resultSet
  }
  updateQuestionnaires(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/calculator-questionnaire/answers`
    return axios.put(endpoint, payload)
  }
  batchSaveQuestionnaires(patientId: string, batchGroup: string,
    questionnaires: Array<{ name: string, value: string }>, programName?: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/questionnaires/batch`
    return axios.put(endpoint, {
      batch_group: batchGroup,
      questionnaires,
      program_name: programName || '',
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    })
  }
  postNotes(payload: any) {
    const endpoint = `${apiEndpoint}/notes`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  getNotes(page: string, limit: string, entityRef: string, entityId: string) {
    const endpoint = `${apiEndpoint}/notes?page=${page}&limit=${limit}&entity_ref=${entityRef}&entity_id=${entityId}`
    return axios.get(endpoint)
  }
  // Scratch-pad entries — clinical items the consumer intake flow flagged
  // for review (unresolved/assumed meds, free-form clinical mentions).
  // patientRefId is the ref_id; the backend resolves it via @check_patient_status.
  getScratchPadEntries(patientRefId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/scratch-pad`
    return axios.get(endpoint)
  }
  // ── Bespoke symptom/questionnaire tracking. The picker (tox ∪ *__attr) is shared
  //    by the experiment-plan readout add and the a-la-carte "+ track a symptom"
  //    affordance on the Toxicity group. patientRefId is the ref_id; the backend
  //    resolves it via @check_patient_status.
  getTrackablePicker(patientRefId: string, search: string = '') {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/tox-questionnaires` +
      (search ? `?name=${encodeURIComponent(search)}` : '')
    return axios.get(endpoint)
  }
  addTrackedQuestionnaire(patientRefId: string, questionnaireName: string) {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/tracked-questionnaires`
    return axios.post(endpoint, { questionnaire_name: questionnaireName }, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    })
  }
  removeTrackedQuestionnaire(patientRefId: string, questionnaireName: string) {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/tracked-questionnaires/` +
      `${encodeURIComponent(questionnaireName)}`
    return axios.delete(endpoint)
  }
  triggerFHIRPatientSync(payload: any) {
    const endpoint = `${apiEndpoint}/epic-fhir-oauth/patients/sync`
    return axios.post(endpoint, payload)
  }
  getMedicationSchedule(patientId: any, params: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/best_possible_schedules/${params}`
    return axios.get(endpoint)
  }
  getMonitorList() {
    const endpoint = `${apiEndpoint}/monitor-names`
    return axios.get(endpoint)
  }
  createMonitor(enrollment_id: any, monitor_name: any) {
    const endpoint = `${apiEndpoint}/monitors/create/${enrollment_id}/ref/${monitor_name}`
    return axios.post(endpoint)
  }
  getLifestylesList() {
    const endpoint = `${apiEndpoint}/lifestyle-names`
    return axios.get(endpoint)
  }
  createLifestyle(enrollment_id: any, lifestyle_name: any) {
    const endpoint = `${apiEndpoint}/lifestyles/create/${enrollment_id}/ref/${lifestyle_name}`
    return axios.post(endpoint)
  }
  changeProgramStageByName(stage_name: string, enrollment_id: string) {
    const endpoint = `${apiEndpoint}/patient/stage_transition/${stage_name}/enrollment/${enrollment_id}`
    return axios.get(endpoint)
  }
  changeStageByName(pId: string, prg: string, stage: string) {
    const endpoint = `${apiEndpoint}/patients/${pId}/${prg}/manual-change-in-stage/${stage}`
    return axios.put(endpoint, {}, JSON_HEADERS)
  }
  getMonitorDetails(pId: string, monitor_id: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/monitors/${monitor_id}`
    return axios.get(endpoint)
  }
  // Triage: symptom report, triage context, monitors, dechallenge trials
  searchSymptomConcepts(query: string) {
    const endpoint = `${apiEndpoint}/symptom-concepts?name=${encodeURIComponent(query)}`
    return axios.get(endpoint)
  }
  getSymptomTriageContext(pId: string, symptomConcept: string, severity?: number, symptomOnsetDate?: number) {
    let endpoint = `${apiEndpoint}/patients/${pId}/symptom-triage-context?symptom_concept=${encodeURIComponent(symptomConcept)}`
    if (severity) endpoint += `&severity=${severity}`
    if (symptomOnsetDate) endpoint += `&symptom_onset_date=${symptomOnsetDate}`
    return axios.get(endpoint)
  }
  getTriageProgramStatus(pId: string, evalId: number) {
    const endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations/${evalId}/triage-program`
    return axios.get(endpoint)
  }
  launchTriageProgram(pId: string, evalId: number) {
    const endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations/${evalId}/launch-triage`
    return axios.post(endpoint, {}, JSON_HEADERS)
  }
  runSymptomEvalTriage(pId: string, evalId: number) {
    const endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations/${evalId}/run-triage`
    return axios.post(endpoint, {}, JSON_HEADERS)
  }
  reportSymptom(pId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/reported-symptoms`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  getQuestionnaireJson(name: string) {
    const endpoint = `${apiEndpoint}/questionnaire-json/${encodeURIComponent(name)}`
    return axios.get(endpoint)
  }
  // Symptom Evaluations
  createSymptomEvaluation(pId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  getSymptomEvaluations(pId: string, phase?: string) {
    let endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations`
    if (phase) endpoint += `?phase=${phase}`
    return axios.get(endpoint)
  }
  getSymptomEvaluationsEnriched(pId: string, phase?: string) {
    let endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations/enriched`
    if (phase) endpoint += `?phase=${phase}`
    return axios.get(endpoint)
  }
  updateSymptomEvaluation(pId: string, evalId: number, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations/${evalId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getAvailablePlans(pId: string, evalId: number) {
    const endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations/${evalId}/available-plans`
    return axios.get(endpoint)
  }
  approveSymptomEvalAction(pId: string, evalId: number, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/symptom-evaluations/${evalId}/approve-action`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  createDechallengeTrial(pId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/dechallenge-trials`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  resolveDechallengeTrial(pId: string, trialId: number, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/dechallenge-trials/${trialId}/resolve`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }
  getDechallengeTrials(pId: string, status?: string) {
    let endpoint = `${apiEndpoint}/patients/${pId}/dechallenge-trials`
    if (status) endpoint += `?status=${status}`
    return axios.get(endpoint)
  }
  getLifestyleDetails(pId: string, lifestyle_id: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/lifestyles/${lifestyle_id}`
    return axios.get(endpoint)
  }
  stopMonitorDetails(pId: string, monitor_id: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/monitors/${monitor_id}`
    return axios.put(endpoint, payload)
  }
  terminateMonitor(monitor_id: any, payload: { reason: string; note?: string }) {
    const endpoint = `${apiEndpoint}/monitors/${monitor_id}/terminate`
    return axios.post(endpoint, payload)
  }
  updateMonitorSeverity(monitor_id: any, severity: number) {
    const endpoint = `${apiEndpoint}/monitors/${monitor_id}/update-severity`
    return axios.post(endpoint, { severity }, { headers: { Accept: 'application/json' } })
  }
  stopLifestyleDetails(pId: string, lifestyle_id: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/lifestyles/${lifestyle_id}`
    return axios.put(endpoint, payload)
  }
  deleteMonitorDetails(pId: string, monitor_id: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/monitors/${monitor_id}`
    return axios.delete(endpoint, {})
  }
  deleteLifestyleDetails(pId: string, lifestyle_id: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/lifestyles/${lifestyle_id}`
    return axios.delete(endpoint, {})
  }
  getMonitorvaluation(id: string) {
    const endpoint = `${apiEndpoint}/monitor_evaluation/${id}`
    return axios.get(endpoint, {})
  }
  createEnrollment(pId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/enrollment/create`
    return axios.post(endpoint, payload)
  }
  getProblemsList() {
    const endpoint = `${apiEndpoint}/problem-names`
    return axios.get(endpoint)
  }
  createProblem(pId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/problems`
    return axios.post(endpoint, payload)
  }
  deleteProblemDetails(pId: string, problemId: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/problems/${problemId}`
    return axios.delete(endpoint, {})
  }
  updateProblem(pId: string, prbId: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/problems/${prbId}/add-program`
    return axios.post(endpoint, payload)
  }
  removeEnrollment(pId: string, prgId: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/enrollments/${prgId}`
    return axios.delete(endpoint, {})
  }
  updateLifestyle(pId: string, lifestyle_id: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/lifestyles/${lifestyle_id}`
    return axios.post(endpoint, payload)
  }
  extendMonitor(pId: string, mId: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/monitors/${mId}/extend`
    return axios.put(endpoint, payload)
  }
  extendLifestyle(pId: string, lifestyle_id: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/lifestyles/${lifestyle_id}/extend`
    return axios.put(endpoint, payload)
  }
  getMedEvidence(pId: string, program: any, med_id: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/programs/${program}/${med_id}`
    return axios.get(endpoint, {})
  }
  getMedReconcilation(id: any, code: any) {
    const endpoint = `${apiEndpoint}/patients/${id}/reconciliate-medications`
    return axios.get(endpoint, {})
  }
  confirmMedReconcilation(pId: string, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${pId}/reconciliate-medications`
    return axios.put(endpoint, payload)
  }
  deletePatient(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}`
    return axios.delete(endpoint, {})
  }
  getTestsData() {
    const endpoint = `${apiEndpoint}/kb-tests`
    return axios.get(endpoint)
  }
  getDiagnosticStatus(program_name: any) {
    const endpoint = `${apiEndpoint}/programs/${program_name}/get_diagnosis`
    return axios.get(endpoint)
  }
  putDiagnosisStatus(patientId: any, diaId: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/update_diagnosis_plan/${diaId}`
    return axios.put(endpoint, payload)
  }
  getDiagnosticTests(refId: any) {
    const endpoint = `${apiEndpoint}/patients/${refId}/diagnostic-tests`
    return axios.get(endpoint)
  }
  prioritizeTests(refId: any) {
    const endpoint = `${apiEndpoint}/patients/${refId}/prioritize-tests`
    return axios.post(endpoint)
  }
  recalculateDiagnosisDetails(patient_id: any, program: any) {
    const endpoint = `${apiEndpoint}/patients/${patient_id}/recalculate_plan/${program}`
    return axios.get(endpoint)
  }
  getBatchGroups() {
    const endpoint = `${apiEndpoint}/kb/batch_groups`
    return axios.get(endpoint)
  }
  postDiagnosticTests(patient_id: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patient_id}/diagnostic-tests`
    return axios.post(endpoint, payload)
  }
  getDiagnosticTest(testId: any) {
    const endpoint = `${apiEndpoint}/diagnostic-tests/${testId}`
    return axios.get(endpoint)
  }
  putDiagnosticTest(test_id: any, payload: any) {
    const endpoint = `${apiEndpoint}/diagnostic-tests/${test_id}`
    return axios.put(endpoint, payload)
  }
  pushTestOrderToElation(test_id: any, vendor?: string) {
    const endpoint = `${apiEndpoint}/diagnostic-tests/${test_id}/elation-order`
    // `vendor` is REQUIRED for a lab test and ignored for imaging/cardiac: each lab prints
    // its own catalog number on the requisition, so the same test resolves to a different
    // code at LabCorp vs Quest. The backend refuses an unmapped (test, vendor) pair rather
    // than substituting another vendor's code.
    return axios.post(endpoint, vendor ? { vendor } : {})
  }
  pushLabOrderToElation(patientId: any, testIds: any[]) {
    // Draft Elation lab orders for many tests, GROUPED BY EACH TEST'S OWN SITE — no vendor
    // argument, because the Site is already on the test (navigator's "Screen and Select
    // testing site" task) and shown on the card. Batching spares the provider eight
    // signatures and eight faxes; the receiving site consolidates the draws itself.
    // Non-lab tests and tests without a site are skipped and reported; a lab test with no
    // catalog number at its site refuses that site's order, and `error.failures[]` names
    // each offender with its site.
    const endpoint = `${apiEndpoint}/patients/${patientId}/lab-order`
    return axios.post(endpoint, { test_ids: testIds })
  }
  putDiagnosticTestResult(patientId: any, payload: any) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/add_test_report/results_available`
    return axios.put(endpoint, payload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }
  deleteDiagnosticTest(patientId: string, id: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/tests/${id}`
    return axios.delete(endpoint, {})
  }
  addLabReport(patientId: string, payload: any) {
    const endpoint = `${apiEndpoint}/elation/patients/${patientId}/import_lab_report_pdf`
    return axios.post(endpoint, payload)
  }
  getReportFileNames() {
    const todays_date = moment().format('YYYY-MM-DD')
    const endpoint = `${apiEndpoint}/tasks/reports/assignments?todays_date=${todays_date}`
    return axios.get(endpoint)
  }
  downloadTaskReport(element: any) {
    const payload = { file_name: element }
    const todays_date = moment().format('YYYY-MM-DD')
    const endpoint = `${apiEndpoint}/download-task-report?todays_date=${todays_date}`
    return axios.post(endpoint, payload, { responseType: 'blob' })
  }
  downloadRetrospectiveTaskReport(date: string) {
    const endpoint = `${apiEndpoint}/tasks/reports/retrospective?date=${date}`
    return axios.get(endpoint, { responseType: 'blob' })
  }
  getTestSites() {
    const endpoint = `${apiEndpoint}/sites`
    return axios.get(endpoint)
  }
  addSite(payload: any) {
    const endpoint = `${apiEndpoint}/sites`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }
  updateSite(siteId: any, payload: any) {
    const endpoint = `${apiEndpoint}/sites/${siteId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }

  /**
   * Get a single site by ID
   */
  getSite(siteId: number) {
    const endpoint = `${apiEndpoint}/sites/${siteId}`
    return axios.get(endpoint)
  }

  // ============== NPI Lookup APIs ==============

  /**
   * Look up provider information from CMS NPI Registry
   */
  lookupNPI(npi: string) {
    const endpoint = `${apiEndpoint}/providers/npi-lookup/${npi}`
    return axios.get(endpoint)
  }

  /**
   * Search NPI Registry by name/location
   */
  searchNPI(params: { first_name?: string; last_name?: string; organization_name?: string; state?: string; city?: string; taxonomy_description?: string; limit?: number }) {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.append(key, String(value))
      }
    })
    const endpoint = `${apiEndpoint}/providers/npi-search?${queryParams.toString()}`
    return axios.get(endpoint)
  }

  // ============== Extended Provider APIs ==============

  /**
   * Search local provider database by name or NPI.
   * This searches our cleaned/validated provider data first.
   */
  searchProvidersLocal(query: string, limit: number = 20) {
    const queryParams = new URLSearchParams()
    queryParams.append('query', query)
    queryParams.append('limit', String(limit))
    const endpoint = `${apiEndpoint}/providers/search?${queryParams.toString()}`
    return axios.get(endpoint)
  }

  /**
   * Get a single provider with their associated sites
   */
  getProvider(providerId: number) {
    const endpoint = `${apiEndpoint}/providers/${providerId}`
    return axios.get(endpoint)
  }

  /**
   * Get provider by NPI from local database
   */
  getProviderByNPI(npi: string) {
    const endpoint = `${apiEndpoint}/providers/by-npi/${npi}`
    return axios.get(endpoint)
  }

  /**
   * Create a new provider
   */
  createProvider(payload: {
    first_name: string
    last_name: string
    specialty: string
    npi?: string
    prefix?: string
    middle_name?: string
    suffix?: string
    credential?: string
    taxonomy_code?: string
    taxonomy_display?: string
    direct_email?: string
  }) {
    const endpoint = `${apiEndpoint}/providers`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }

  /**
   * Update an existing provider
   */
  updateProvider(providerId: number, payload: any) {
    const endpoint = `${apiEndpoint}/providers/${providerId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }

  // ============== Site Proximity Search APIs ==============

  /**
   * Search for sites near a zipcode, optionally filtered by diagnostic tests
   */
  searchSitesByProximity(params: { zipcode: string; radius_miles?: number; tests?: string[]; require_all_tests?: boolean; exclude_placeholders?: boolean; limit?: number }) {
    const queryParams = new URLSearchParams()
    queryParams.append('zipcode', params.zipcode)
    if (params.radius_miles) queryParams.append('radius_miles', String(params.radius_miles))
    if (params.tests?.length) queryParams.append('tests', params.tests.join(','))
    if (params.require_all_tests) queryParams.append('require_all_tests', 'true')
    if (params.exclude_placeholders !== undefined) {
      queryParams.append('exclude_placeholders', String(params.exclude_placeholders))
    }
    if (params.limit) queryParams.append('limit', String(params.limit))

    const endpoint = `${apiEndpoint}/sites/search-by-proximity?${queryParams.toString()}`
    return axios.get(endpoint)
  }

  /**
   * Search sites by name, address, city, state, or zip.
   * Use 'query' for general search across name and address fields.
   */
  searchSites(params: { query?: string; name?: string; address?: string; city?: string; state?: string; zip_code?: string; limit?: number }) {
    const queryParams = new URLSearchParams()
    // General search query - searches name AND address
    if (params.query) queryParams.append('q', params.query)
    // Specific field searches
    if (params.name) queryParams.append('name', params.name)
    if (params.address) queryParams.append('address', params.address)
    if (params.city) queryParams.append('city', params.city)
    if (params.state) queryParams.append('state', params.state)
    if (params.zip_code) queryParams.append('zip_code', params.zip_code)
    if (params.limit) queryParams.append('limit', String(params.limit))

    const endpoint = `${apiEndpoint}/sites?${queryParams.toString()}`
    return axios.get(endpoint)
  }

  /**
   * Search sites by phone or fax number
   */
  searchSitesByPhone(phone: string, limit?: number) {
    const queryParams = new URLSearchParams()
    queryParams.append('phone', phone)
    if (limit) queryParams.append('limit', String(limit))

    const endpoint = `${apiEndpoint}/sites/search-by-phone?${queryParams.toString()}`
    return axios.get(endpoint)
  }

  /**
   * Get all sites associated with a provider
   */
  getProviderSites(providerId: number) {
    const endpoint = `${apiEndpoint}/providers/${providerId}/sites`
    return axios.get(endpoint)
  }

  /**
   * Associate a site with a provider
   */
  associateProviderSite(providerId: number, siteId: number) {
    const endpoint = `${apiEndpoint}/providers/${providerId}/sites/${siteId}`
    return axios.post(endpoint)
  }

  /**
   * Unassociate a site from a provider
   */
  unassociateProviderSite(providerId: number, siteId: number) {
    const endpoint = `${apiEndpoint}/providers/${providerId}/sites/${siteId}`
    return axios.delete(endpoint)
  }

  /**
   * Get list of available diagnostic tests for site filtering
   */
  getAvailableTests() {
    const endpoint = `${apiEndpoint}/sites/available-tests`
    return axios.get(endpoint)
  }

  /**
   * Create a new site
   */
  createSite(siteData: {
    name: string
    address?: string
    address_2?: string
    city?: string
    state?: string
    zip_code?: string
    phone?: string
    fax?: string
    email?: string
    npi?: string
    tests_offered?: string
    is_placeholder?: boolean
    comment?: string
    diagnostic_site_rating?: number
    tests_outreach_date?: string
    tests_outreach_outcome?: string
  }) {
    const endpoint = `${apiEndpoint}/sites`
    return axios.post(endpoint, siteData)
  }

  /**
   * Check multiple candidate sites for duplicates (bulk add workflow)
   */
  bulkCheckSites(
    candidates: Array<{
      name: string
      address?: string
      city?: string
      state?: string
      zip_code?: string
      phone?: string
    }>
  ) {
    const endpoint = `${apiEndpoint}/sites/bulk-check`
    return axios.post(endpoint, { candidates }, JSON_HEADERS)
  }

  // ============== Site Phone APIs ==============

  /**
   * Get all phone/fax numbers for a site
   * IMPORTANT: Use this instead of single phone/fax fields
   */
  getSitePhones(siteId: number) {
    const endpoint = `${apiEndpoint}/sites/${siteId}/phones`
    return axios.get(endpoint)
  }

  /**
   * Get all providers linked to a site
   */
  getSiteProviders(siteId: number) {
    const endpoint = `${apiEndpoint}/sites/${siteId}/providers`
    return axios.get(endpoint)
  }

  /**
   * Add a phone/fax number to a site
   */
  addSitePhone(
    siteId: number,
    payload: {
      number: string
      number_type: 'phone' | 'fax'
      purpose?: string
      notes?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/sites/${siteId}/phones`
    return axios.post(endpoint, payload, JSON_HEADERS)
  }

  /**
   * Update a site phone/fax entry (purpose, notes)
   */
  updateSitePhone(
    sitePhoneId: number,
    payload: {
      purpose?: string
      notes?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/site-phones/${sitePhoneId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }

  /**
   * Delete a site phone/fax entry
   */
  deleteSitePhone(sitePhoneId: number) {
    const endpoint = `${apiEndpoint}/site-phones/${sitePhoneId}`
    return axios.delete(endpoint)
  }

  /**
   * Confirm a site phone/fax number
   * Sets confirmed_at to current time
   */
  confirmSitePhone(sitePhoneId: number, confirmationSource: string = 'manual') {
    const endpoint = `${apiEndpoint}/site-phones/${sitePhoneId}/confirm`
    return axios.post(endpoint, { confirmation_source: confirmationSource }, JSON_HEADERS)
  }

  /**
   * Get phones needing confirmation (never confirmed or >1 year stale)
   */
  getPhonesNeedingConfirmation(limit?: number, numberType?: 'phone' | 'fax') {
    const params = new URLSearchParams()
    if (limit) params.append('limit', String(limit))
    if (numberType) params.append('number_type', numberType)
    const endpoint = `${apiEndpoint}/site-phones/needs-confirmation?${params.toString()}`
    return axios.get(endpoint)
  }

  /**
   * Get phone numbers shared by multiple sites
   */
  getSharedPhoneNumbers(limit?: number) {
    const params = new URLSearchParams()
    if (limit) params.append('limit', String(limit))
    const endpoint = `${apiEndpoint}/site-phones/shared?${params.toString()}`
    return axios.get(endpoint)
  }

  // ============== Patient Care Team APIs ==============

  /**
   * Get patient's care team (providers at specific sites)
   */
  getPatientCareTeam(patientId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/care-team`
    return axios.get(endpoint)
  }

  /**
   * Add a provider-at-site to patient's care team
   */
  addCareTeamMember(
    patientId: string,
    payload: {
      provider_id: number
      // Optional: a provider with no linked site is a legitimate care team member
      // (RD 2026-08-08). Omit the key rather than sending 0.
      site_id?: number
      role?: string
      is_primary?: boolean
      is_validated?: boolean
      is_referring?: boolean
      notes?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/care-team`
    return axios.post(endpoint, payload)
  }

  /**
   * Update a patient care team entry
   */
  updateCareTeamMember(
    patientProviderSiteId: number,
    payload: {
      role?: string
      is_primary?: boolean
      // Setting this clears the flag on the patient's other memberships server-side, so
      // moving the star is one call rather than clear-then-set.
      is_referring?: boolean
      is_validated?: boolean
      notes?: string
      site_id?: number
    }
  ) {
    const endpoint = `${apiEndpoint}/patient-provider-sites/${patientProviderSiteId}`
    return axios.put(endpoint, payload, JSON_HEADERS)
  }

  /**
   * Remove a provider from patient's care team
   */
  removeCareTeamMember(patientProviderSiteId: number) {
    const endpoint = `${apiEndpoint}/patient-provider-sites/${patientProviderSiteId}`
    return axios.delete(endpoint)
  }

  // =============================================================================
  // Pharmacy APIs
  // =============================================================================

  /**
   * Search pharmacies by name, city, or state
   */
  searchPharmacies(params: { query?: string; city?: string; state?: string; limit?: number }) {
    const endpoint = `${apiEndpoint}/pharmacies/search`
    return axios.get(endpoint, { params })
  }

  /**
   * Get a pharmacy by ID
   */
  getPharmacy(pharmacyId: number) {
    const endpoint = `${apiEndpoint}/pharmacies/${pharmacyId}`
    return axios.get(endpoint)
  }

  /**
   * Get a pharmacy by NCPDP ID
   */
  getPharmacyByNCPDP(ncpdpId: string) {
    const endpoint = `${apiEndpoint}/pharmacies/by-ncpdp/${ncpdpId}`
    return axios.get(endpoint)
  }

  /**
   * Create a new pharmacy
   */
  createPharmacy(payload: {
    ncpdp_id: string
    store_name: string
    address_1?: string
    address_2?: string
    city?: string
    state?: string
    postal_code?: string
    phone?: string
    fax?: string
    npi?: string
    specialty_types?: string
    elation_id?: number
  }) {
    const endpoint = `${apiEndpoint}/pharmacies`
    return axios.post(endpoint, payload)
  }

  /**
   * Get all pharmacies for a patient
   */
  getPatientPharmacies(patientId: number | string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/pharmacies`
    return axios.get(endpoint)
  }

  /**
   * Add a pharmacy to a patient
   */
  addPatientPharmacy(
    patientId: number | string,
    payload: {
      pharmacy_id: number
      is_preferred?: boolean
      notes?: string
      source?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/pharmacies`
    return axios.post(endpoint, payload)
  }

  /**
   * Remove a pharmacy from a patient
   */
  removePatientPharmacy(patientId: number | string, patientPharmacyId: number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/pharmacies/${patientPharmacyId}`
    return axios.delete(endpoint)
  }

  /**
   * Update a patient pharmacy association
   */
  updatePatientPharmacy(
    patientId: number | string,
    patientPharmacyId: number,
    payload: {
      is_preferred?: boolean
      notes?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/pharmacies/${patientPharmacyId}`
    return axios.patch(endpoint, payload)
  }

  /**
   * Set a pharmacy as the patient's preferred pharmacy
   */
  setPreferredPharmacy(patientId: number | string, patientPharmacyId: number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/pharmacies/${patientPharmacyId}/set-preferred`
    return axios.post(endpoint)
  }

  // ========== ELATION PUSH ENDPOINTS ==========

  /**
   * Get Elation push status for a patient
   * Returns what can be pushed (care team, pharmacies) and whether Elation is configured
   */
  getElationPushStatus(patientId: number | string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/elation/status`
    return axios.get(endpoint)
  }

  /**
   * Push validated care team members to Elation
   * Only pushes members that are validated and have NPI
   */
  pushCareTeamToElation(patientId: number | string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/elation/push-care-team`
    return axios.post(endpoint)
  }

  /**
   * Push pharmacies to Elation
   * Pushes up to 2 pharmacies (preferred first) that have NCPDP IDs
   */
  pushPharmaciesToElation(patientId: number | string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/elation/push-pharmacies`
    return axios.post(endpoint)
  }

  /**
   * Import care team from Elation Surescripts CCDA documents
   * Fetches encounter data, creates providers/sites, and links to patient's care team
   */
  importCareTeamFromElation(patientId: number | string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/import-care-team`
    return axios.post(endpoint)
  }

  // ========== SITE DEDUPLICATION ENDPOINTS ==========

  /**
   * Get deduplication statistics
   * Returns counts of potential duplicates by type (address, phone, etc.)
   */
  getDeduplicationStats() {
    const endpoint = `${apiEndpoint}/sites/deduplication/stats`
    return axios.get(endpoint)
  }

  /**
   * Get duplicate site groups
   * Returns groups of sites that are potential duplicates
   */
  getDeduplicationGroups(params?: { match_type?: 'address' | 'phone' | 'any'; limit?: number; offset?: number }) {
    const endpoint = `${apiEndpoint}/sites/deduplication/groups`
    const queryParams = new URLSearchParams()
    if (params?.match_type) queryParams.append('match_type', params.match_type)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    const queryString = queryParams.toString()
    return axios.get(queryString ? `${endpoint}?${queryString}` : endpoint)
  }

  /**
   * Find similar sites for a specific site
   * Used to find merge candidates
   */
  findSimilarSites(siteId: number, includePlaceholders: boolean = true) {
    const endpoint = `${apiEndpoint}/sites/${siteId}/similar?include_placeholders=${includePlaceholders}`
    return axios.get(endpoint)
  }

  /**
   * Suggest a merge target for a site (typically a placeholder)
   * Returns the best candidate real site to merge into
   */
  suggestMergeTarget(siteId: number) {
    const endpoint = `${apiEndpoint}/sites/${siteId}/suggest-merge`
    return axios.get(endpoint)
  }

  /**
   * Merge source site into target site
   * Transfers all relationships and marks source as merged
   */
  mergeSites(sourceSiteId: number, targetSiteId: number) {
    const endpoint = `${apiEndpoint}/sites/${sourceSiteId}/merge/${targetSiteId}`
    return axios.post(endpoint)
  }

  /**
   * Get placeholder site statistics by pattern
   */
  getPlaceholderStats() {
    const endpoint = `${apiEndpoint}/sites/placeholders/stats`
    return axios.get(endpoint)
  }

  /**
   * List placeholder sites with optional filtering
   * @param params.pattern - Filter by pattern: 'npi', 'surescripts', 'other', or omit for all
   * @param params.search - Filter by name substring
   * @param params.limit - Max results (default 100)
   * @param params.offset - Pagination offset (default 0)
   */
  listPlaceholderSites(
    params: {
      pattern?: 'npi' | 'surescripts' | 'other'
      search?: string
      limit?: number
      offset?: number
    } = {}
  ) {
    const endpoint = `${apiEndpoint}/sites/placeholders`
    const queryParams = new URLSearchParams()
    if (params.pattern) queryParams.append('pattern', params.pattern)
    if (params.search) queryParams.append('search', params.search)
    if (params.limit) queryParams.append('limit', String(params.limit))
    if (params.offset) queryParams.append('offset', String(params.offset))
    const queryString = queryParams.toString()
    return axios.get(queryString ? `${endpoint}?${queryString}` : endpoint)
  }

  // ==============================
  // RCM (Revenue Cycle Management) Methods
  // ==============================

  /**
   * Get all clinic visits (encounters) for a patient
   * @param patientId - Patient ID
   * @param params.rcm_enabled - Filter by RCM enabled status
   * @param params.status - Filter by visit status
   */
  getPatientEncounters(patientId: string | number, params: { rcm_enabled?: boolean; status?: string } = {}) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/clinic-visits`
    const queryParams = new URLSearchParams()
    if (params.rcm_enabled !== undefined) {
      queryParams.append('rcm_enabled', String(params.rcm_enabled))
    }
    if (params.status) queryParams.append('status', params.status)
    const queryString = queryParams.toString()
    return axios.get(queryString ? `${endpoint}?${queryString}` : endpoint)
  }

  /**
   * Sync encounters from Elation for a patient
   * Creates ClinicVisitLog entries for completed Elation appointments
   * @param patientId - Patient ID
   * @param fromDate - Optional start date filter (YYYY-MM-DD)
   */
  syncEncountersFromElation(patientId: string | number, fromDate?: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/clinic-visits/sync-elation`
    return axios.post(endpoint, { from_date: fromDate })
  }

  /**
   * Get RCM workflow status for an encounter
   * @param visitId - ClinicVisitLog ID
   */
  getRCMWorkflowStatus(visitId: number) {
    const endpoint = `${apiEndpoint}/encounters/${visitId}/rcm-status`
    return axios.get(endpoint)
  }

  /**
   * Get RCM summary for all encounters of a patient
   * @param patientId - Patient ID
   */
  getPatientRCMSummary(patientId: string | number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rcm-summary`
    return axios.get(endpoint)
  }

  /**
   * Create RCM tasks for an encounter
   * Starts the RCM workflow by creating the initial claim submission task
   * @param visitId - ClinicVisitLog ID
   */
  createRCMTasksForEncounter(visitId: number) {
    const endpoint = `${apiEndpoint}/encounters/${visitId}/rcm-tasks`
    return axios.post(endpoint)
  }

  /**
   * Complete an RCM task with an outcome
   * Automatically creates the next task in the workflow based on outcome
   * @param taskId - Task ID
   * @param outcome - 'paid', 'denied', 'received', 'not_received'
   * @param note - Optional completion note
   */
  completeRCMTask(taskId: number, outcome: string, note?: string) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}/rcm-complete`
    return axios.post(endpoint, { outcome, note })
  }

  // ==============================
  // RCM Dashboard Methods
  // ==============================

  /**
   * Get RCM dashboard data (claims + stats for a billing month)
   */
  getRCMDashboard(params?: { billing_month?: string; hospital_name?: string }) {
    const endpoint = `${apiEndpoint}/rcm/dashboard`
    return axios.get(endpoint, { params })
  }

  /**
   * Get paginated RCM claims list with filters
   */
  getRCMClaims(params?: {
    page?: number
    limit?: number
    billing_month?: string
    hospital_name?: string
    rcm_status?: string
    payer_name?: string
    sort_by?: string
    sort_order?: string
  }) {
    const endpoint = `${apiEndpoint}/rcm/claims`
    return axios.get(endpoint, { params })
  }

  /**
   * Export RCM claims to CSV
   */
  exportRCMClaims(params: {
    billing_month?: string
    hospital_name?: string
    rcm_status?: string
  }) {
    const endpoint = `${apiEndpoint}/rcm/claims/export`
    return axios.post(endpoint, params, { responseType: 'blob' })
  }

  /**
   * Get encounters with server-side filtering and pagination
   * GET /patient/encounters
   */
  getEncounters(params?: { date_filter?: 'today' | 'yesterday' | 'tomorrow'; from_date?: string; to_date?: string; hospital_name?: string; page?: number; per_page?: number; sort_by?: string; sort_dir?: string }) {
    const endpoint = `${apiEndpoint}/patient/encounters`
    return axios.get(endpoint, { params })
  }

  /**
   * Bulk sync Elation encounters for all RCM-eligible patients
   */
  /** Bulk encounter + readiness sync from Elation.
   *  - from_date: optional cutoff (defaults to 6 months ago)
   *  - fields:    comma-separated subset of {ccd,labs,insurance,address}.
   *               Default = all four. RevOps Worklist only needs
   *               'insurance' for the Payer column + LIKELY_NOT_SEEN gate;
   *               that mode runs ~3x faster (1 Elation call/patient instead of 3). */
  syncAllRCMEncounters(params?: { from_date?: string; fields?: string }) {
    const endpoint = `${apiEndpoint}/rcm/sync-all`
    return axios.post(endpoint, params || {})
  }

  /**
   * Bulk sync Elation medications for all RCM-eligible patients. Prerequisite
   * for the Meds tab to have data. Runs the per-patient insert_patient_medications
   * for every LifeMD + Boylston patient with a real Elation external_id.
   */
  syncAllRCMMedications() {
    const endpoint = `${apiEndpoint}/rcm/meds/sync-all`
    return axios.post(endpoint, {})
  }

  // ==========================================================================
  // RevOps — Encounter Worklist (Revenue & Operations Console)
  // ==========================================================================

  /** The unified worklist: every encounter's done/not-done state + flags. */
  getWorklist(params?: {
    from_date?: string
    to_date?: string
    triage_role?: string
    flag?: string
    search?: string
    page?: number
    limit?: number
    sort_by?: string
    sort_order?: string
  }) {
    const endpoint = `${apiEndpoint}/rcm/worklist`
    return axios.get(endpoint, { params })
  }

  /** Rebuild the worklist (enqueues Celery when enabled, else runs inline). */
  refreshWorklist(body?: {
    from_date?: string; to_date?: string; live?: boolean;
    include_elation?: boolean; include_era?: boolean; include_notes?: boolean;
  }) {
    const endpoint = `${apiEndpoint}/rcm/worklist/refresh`
    return axios.post(endpoint, body || {})
  }

  /** TEMP diagnostic (DM-7740): read-only, PHI-free encounter-pipeline health
   *  report for the "Download diagnostic report" button. Authenticated via the
   *  normal bearer interceptor — no shared-secret token in the browser. The
   *  backend route is gated by REVOPS_DIAG_ENABLED + an email allowlist
   *  (REVOPS_DIAG_ALLOWED_EMAILS); a non-allowlisted caller gets 403. */
  getRcmPipelineReport(date?: string) {
    const endpoint = `${apiEndpoint}/rcm/diag/pipeline-report`
    return axios.get(endpoint, { params: date ? { date } : {} })
  }

  /** Set / revoke a "stop flagging this row" override on an encounter. */
  setWorklistOverride(
    apptId: string,
    body: { override_type: string; reason?: string; active?: boolean }
  ) {
    const endpoint = `${apiEndpoint}/rcm/worklist/${apptId}/override`
    return axios.post(endpoint, body)
  }

  // ── ERA ingestion + review ──────────────────────────────────────────
  /** Upload an ERA PDF (multipart). Parsed into remittance rows server-side. */
  uploadERA(file: File) {
    const form = new FormData()
    form.append('file', file)
    return axios.post(`${apiEndpoint}/rcm/era/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  getERADocuments() {
    return axios.get(`${apiEndpoint}/rcm/era/documents`)
  }

  getERARows(docId: number) {
    return axios.get(`${apiEndpoint}/rcm/era/documents/${docId}/rows`)
  }

  /** Fetch the stored ERA PDF as a blob (auth header carried) for in-app viewing. */
  getERAPdfBlob(docId: number) {
    return axios.get(`${apiEndpoint}/rcm/era/documents/${docId}/pdf`, { responseType: 'blob' })
  }

  /** Human edit of a parsed remittance row. Two kinds: a TRANSCRIPTION fix
   * (patient_responsibility — read the PDF, type what it actually says, for
   * PR_PARSER_MISMATCH), and a LINKAGE assertion (elation_mrn — which encounter
   * this remittance belongs to, when the exact-service-date matcher missed).
   * date_of_encounter is deliberately not editable: it transcribes the payer's
   * document, so the stored row stays faithful to the ERA PDF. */
  patchERARow(rowId: number, body: {
    patient_responsibility?: number; elation_bill_id?: string; elation_mrn?: string | null
  }) {
    return axios.patch(`${apiEndpoint}/rcm/era/rows/${rowId}`, body)
  }

  /** Manually trigger parse for an uploaded ERA doc. Used when the upload-time
   * parse was skipped (Celery enabled but no worker) or to re-run after a fix. */
  parseERADocument(docId: number) {
    return axios.post(`${apiEndpoint}/rcm/era/documents/${docId}/parse`, {})
  }

  /** Re-run only the encounter→MRN match step on an already-parsed doc.
   * Use when the doc parsed before its encounters showed up on the worklist. */
  backfillERAMrn(docId: number) {
    return axios.post(`${apiEndpoint}/rcm/era/documents/${docId}/backfill-mrn`, {})
  }

  /** Create draft Stripe invoices for a doc's billable rows. dry_run previews. */
  createERAInvoices(docId: number, dryRun = true) {
    return axios.post(`${apiEndpoint}/rcm/era/documents/${docId}/create-invoices`, { dry_run: dryRun })
  }

  // ── money-moving actions (Phase 3a) — real charges / patient emails ──
  /** Finalize (if draft) + email a Stripe invoice. */
  sendInvoice(invoiceId: string) {
    return axios.post(`${apiEndpoint}/rcm/invoice/${invoiceId}/send`, {})
  }

  /** Cancel an invoice — soft tombstone (metadata.do_not_bill=true) + void in
   * Stripe if status=open. Refuses terminal statuses. */
  cancelInvoice(invoiceId: string, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/invoice/${invoiceId}/cancel`, { reason })
  }

  /** Collect an OPEN invoice from the card on file — the step after Send, once
   * the patient's self-pay window has elapsed. Pays the invoice itself, so it
   * flips to `paid` and drops out of the mail wave. Prefer this over
   * chargeCopay for anything that already has an invoice: chargeCopay leaves
   * the invoice open and the paper wave later duns a patient who already paid. */
  chargeInvoice(invoiceId: string, opts: { grace_days?: number; force?: boolean } = {}) {
    return axios.post(`${apiEndpoint}/rcm/invoice/${invoiceId}/charge`, opts)
  }

  /** Charge a known copay off-session against the patient's saved card.
   * Point-of-visit only — if an invoice exists, use chargeInvoice instead. */
  chargeCopay(body: {
    customer_id: string; amount_cents: number; elation_mrn?: string
    description?: string; idempotency_ref?: string
  }) {
    return axios.post(`${apiEndpoint}/rcm/copay/charge`, body)
  }

  /** Mail an invoice via PostGrid (QR overlay). Auto-finalizes drafts.
   * dry_run previews the merged PDF without mailing.
   * also_email = true → ALSO emails (Send+Mail combined). */
  mailInvoice(invoiceId: string, toContact: string | object, dryRun = true, alsoEmail = false) {
    return axios.post(`${apiEndpoint}/rcm/invoice/${invoiceId}/mail`,
      { to_contact: toContact, dry_run: dryRun, also_email: alsoEmail })
  }

  /** Email a Stripe card-on-file setup link via MS Graph. dry_run returns the link. */
  sendCardSetup(body: { customer_id: string; email: string; name?: string; elation_mrn?: string; dry_run?: boolean }) {
    return axios.post(`${apiEndpoint}/rcm/card-setup`, body)
  }

  /** Ensure a Stripe customer exists for the patient on a worklist row.
   * Runs the 3-tier ladder (cache → metadata search → create). NO emails. */
  ensureStripeCustomerForRow(apptId: string, body: { cohort?: string; dry_run?: boolean } = {}) {
    return axios.post(`${apiEndpoint}/rcm/worklist/${encodeURIComponent(apptId)}/ensure-customer`, body)
  }

  /** Bulk-ensure Stripe customers. Defaults to all rows currently flagged
   * NO_STRIPE_CUSTOMER. Idempotent (cached patients are tier-1 hits). */
  ensureStripeCustomersBulk(body: { patient_ids?: number[]; cohort?: string; dry_run?: boolean } = {}) {
    return axios.post(`${apiEndpoint}/rcm/customers/ensure`, body)
  }

  /** Retire a patient at the collection level — sets all enrollments to
   * PATIENT_STATUS.INACTIVE. Hides from Worklist/Tests/Meds tabs. Reversible. */
  retirePatient(patientId: number, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/patients/${patientId}/retire`, { reason })
  }
  reactivatePatient(patientId: number) {
    return axios.post(`${apiEndpoint}/rcm/patients/${patientId}/reactivate`, {})
  }

  /** Targeted Rebuild — runs the builder for one or a few encounters only.
   *  Use after a stop/resume action so the new state propagates without
   *  triggering the full 5-min cohort rebuild. Body: appt_ids OR patient_id
   *  (patient_id rebuilds all of that patient's materialized rows). */
  rebuildWorklistTargeted(body: { appt_ids?: string[]; patient_id?: number }) {
    return axios.post(`${apiEndpoint}/rcm/worklist/rebuild-targeted`, body)
  }

  /** Terminal: hide encounter row from worklist. Gated by UI on
   * row.can_hide (requires billing/tests/meds already stopped + no open invoice). */
  closeEncounterPursuit(apptId: string, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/worklist/${encodeURIComponent(apptId)}/close-pursuit`, { reason })
  }
  reopenEncounterPursuit(apptId: string) {
    return axios.post(`${apiEndpoint}/rcm/worklist/${encodeURIComponent(apptId)}/reopen-pursuit`, {})
  }

  // ── Stop-pursuing cascades (2026-06-12 design — Worklist is the control surface) ──
  /** Encounter-scope. Adds no_billing_needed override + cancels open Stripe
   * invoices tagged with this encounter's era_icn (do_not_bill + void if open). */
  stopPursuingBilling(apptId: string, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/worklist/${encodeURIComponent(apptId)}/stop-billing`, { reason })
  }
  resumePursuingBilling(apptId: string) {
    return axios.post(`${apiEndpoint}/rcm/worklist/${encodeURIComponent(apptId)}/resume-billing`, {})
  }
  /** Patient-scope. Closes pursuit on every active DiagnosticTest + sets
   * no_followup_needed override on every Worklist row for the patient. */
  stopPursuingTests(patientId: number, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/patients/${patientId}/stop-tests`, { reason })
  }
  resumePursuingTests(patientId: number) {
    return axios.post(`${apiEndpoint}/rcm/patients/${patientId}/resume-tests`, {})
  }
  /** Patient-scope. Closes pursuit on every active Medication + sets
   * no_followup_needed override on every Worklist row for the patient. */
  stopPursuingMeds(patientId: number, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/patients/${patientId}/stop-meds`, { reason })
  }
  resumePursuingMeds(patientId: number) {
    return axios.post(`${apiEndpoint}/rcm/patients/${patientId}/resume-meds`, {})
  }

  // ── test / med lifecycle (Phase 4) ──────────────────────────────────
  getTestLifecycle(params?: {
    status?: string; active_only?: boolean; include_closed?: boolean;
    search?: string; stalled_only?: boolean; test_name?: string;
    limit?: number; cohort?: string;
  }) {
    return axios.get(`${apiEndpoint}/rcm/tests`, { params })
  }

  /** Mark a DiagnosticTest row as "stop pursuing". Preserves the factual
   * TEST_STATUS; hides from active Tests view (reversible). */
  closeTestPursuit(testId: number, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/tests/${testId}/close-pursuit`, { reason })
  }
  reopenTestPursuit(testId: number) {
    return axios.post(`${apiEndpoint}/rcm/tests/${testId}/reopen-pursuit`, {})
  }

  getMedLifecycle(params?: {
    /** One of: 'proposed_not_ordered' | 'ordered_not_initiated' | 'initiated_no_followup' | 'complete' */
    gap?: string;
    include_closed?: boolean;
    include_inactive_patients?: boolean;
    search?: string;
    med_name?: string;
    limit?: number;
  }) {
    return axios.get(`${apiEndpoint}/rcm/meds`, { params })
  }

  /** Mark a Medication row as "stop pursuing". Preserves the factual
   * evaluation_status/titration; hides from active Meds view. Reversible. */
  closeMedPursuit(medId: number, reason: string) {
    return axios.post(`${apiEndpoint}/rcm/meds/${medId}/close-pursuit`, { reason })
  }
  reopenMedPursuit(medId: number) {
    return axios.post(`${apiEndpoint}/rcm/meds/${medId}/reopen-pursuit`, {})
  }

  /** Per-encounter revenue lifecycle (insurance/patient/collection status). */
  getRevenue(params?: {
    balance_only?: boolean;
    search?: string;
    next_action?: string;
    balance_band?: 'small' | 'medium' | 'large';
    needs_resend?: boolean;
    resend_grace_days?: number;
    limit?: number;
  }) {
    return axios.get(`${apiEndpoint}/rcm/revenue`, { params })
  }

  /** Create ONE draft invoice for a single encounter (ERA remittance row). */
  createInvoiceForEncounter(remittanceId: number, dryRun = false) {
    return axios.post(`${apiEndpoint}/rcm/revenue/${remittanceId}/create-invoice`, { dry_run: dryRun })
  }

  // ==========================================================================
  // RPM (Remote Patient Monitoring) Billing APIs
  // ==========================================================================

  /**
   * Get all RPM enrollments for a patient
   * @param patientId - Patient ID
   */
  getRPMEnrollments(patientId: string | number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-enrollments`
    return axios.get(endpoint)
  }

  /**
   * Create RPM enrollment for program enrollment(s)
   * @param patientId - Patient ID (ref_id like "BEE2E0")
   * @param payload - Enrollment data including programs or enrollment_id and consent info
   */
  createRPMEnrollment(
    patientId: string | number,
    payload: {
      programs?: string[]
      enrollment_id?: number
      consent_status?: string
      consent_method?: string
      consent_notes?: string
      billing_provider_id?: number
      billing_npi?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-enrollments`
    return axios.post(endpoint, payload)
  }

  /**
   * Update an RPM enrollment
   * @param patientId - Patient ID
   * @param rpmEnrollmentId - RPM enrollment ID
   * @param payload - Fields to update
   */
  updateRPMEnrollment(
    patientId: string | number,
    rpmEnrollmentId: number,
    payload: {
      status?: string
      consent_status?: string
      consent_method?: string
      consent_notes?: string
      billing_provider_id?: number
      billing_npi?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-enrollments/${rpmEnrollmentId}`
    return axios.put(endpoint, payload)
  }

  /**
   * Disenroll patient from RPM (marks as inactive)
   * @param patientId - Patient ID
   * @param rpmEnrollmentId - RPM enrollment ID
   */
  deleteRPMEnrollment(patientId: string | number, rpmEnrollmentId: number) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-enrollments/${rpmEnrollmentId}`
    return axios.delete(endpoint)
  }

  /**
   * Record consent for RPM enrollment
   * @param patientId - Patient ID
   * @param rpmEnrollmentId - RPM enrollment ID
   * @param payload - Consent details
   */
  recordRPMConsent(
    patientId: string | number,
    rpmEnrollmentId: number,
    payload: {
      consent_status: 'obtained' | 'declined'
      consent_method?: string
      consent_notes?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-enrollments/${rpmEnrollmentId}/consent`
    return axios.post(endpoint, payload)
  }

  // RPM Time Tracking

  /**
   * Get RPM time entries for a patient
   * @param patientId - Patient ID
   * @param params - Optional filters
   */
  getRPMTimeEntries(
    patientId: string | number,
    params?: {
      billing_month?: string
      staff_id?: string
      rpm_enrollment_id?: number
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-time`
    return axios.get(endpoint, { params })
  }

  /**
   * Add a time entry for RPM
   * @param patientId - Patient ID
   * @param payload - Time entry data
   */
  addRPMTimeEntry(
    patientId: string | number,
    payload: {
      rpm_enrollment_id: number
      activity_date: string // YYYY-MM-DD
      duration_seconds?: number // Preferred: exact time in seconds
      duration_minutes?: number // Deprecated: use duration_seconds
      activity_type?: string
      staff_role?: string // 'provider' or 'navigator'
      notes?: string
      is_auto_tracked?: boolean
    }
  ) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-time`
    return axios.post(endpoint, payload)
  }

  /**
   * Update an RPM time entry
   * @param timeEntryId - Time entry ID
   * @param payload - Fields to update
   */
  updateRPMTimeEntry(
    timeEntryId: number,
    payload: {
      duration_minutes?: number
      activity_type?: string
      notes?: string
    }
  ) {
    const endpoint = `${apiEndpoint}/rpm-time/${timeEntryId}`
    return axios.put(endpoint, payload)
  }

  /**
   * Delete an RPM time entry
   * @param timeEntryId - Time entry ID
   */
  deleteRPMTimeEntry(timeEntryId: number) {
    const endpoint = `${apiEndpoint}/rpm-time/${timeEntryId}`
    return axios.delete(endpoint)
  }

  /**
   * Get monthly time summary for a patient
   * @param patientId - Patient ID
   * @param billingMonth - Optional billing month (YYYY-MM-DD)
   */
  getRPMTimeSummary(patientId: string | number, billingMonth?: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-time/summary`
    return axios.get(endpoint, { params: { billing_month: billingMonth } })
  }

  // RPM Reading Tracking

  /**
   * Get RPM reading days for a patient
   * @param patientId - Patient ID
   * @param params - Optional filters
   */
  getRPMReadings(patientId: string | number, params?: { billing_month?: string }) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-readings`
    return axios.get(endpoint, { params })
  }

  /**
   * Get monthly reading summary for a patient
   * @param patientId - Patient ID
   * @param billingMonth - Optional billing month (YYYY-MM-DD)
   */
  getRPMReadingSummary(patientId: string | number, billingMonth?: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-readings/summary`
    return axios.get(endpoint, { params: { billing_month: billingMonth } })
  }

  // RPM Billing

  /**
   * Get RPM billing periods for a patient
   * @param patientId - Patient ID
   * @param billingMonth - Optional filter by month
   */
  getRPMBillingPeriods(patientId: string | number, billingMonth?: string) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/rpm-billing`
    return axios.get(endpoint, { params: { billing_month: billingMonth } })
  }

  /**
   * Get RPM dashboard data
   * @param params - Optional filters
   */
  getRPMDashboard(params?: { billing_month?: string; hospital_name?: string }) {
    const endpoint = `${apiEndpoint}/rpm/dashboard`
    return axios.get(endpoint, { params })
  }

  /**
   * Generate RPM billing report
   * @param params - Report parameters
   */
  generateRPMBillingReport(params: { billing_month: string; hospital_name?: string }) {
    const endpoint = `${apiEndpoint}/rpm-billing/generate-report`
    return axios.post(endpoint, params, { responseType: 'blob' })
  }

  submitSigFeedback(patientId: string, medId: number, payload: { sig_rating: boolean; feedback_note?: string }) {
    const endpoint = `${apiEndpoint}/patients/${patientId}/medications/${medId}/sig-feedback`
    return axios.put(endpoint, payload)
  }

  exportAiFeedback(type: string): Promise<Blob> {
    return axios.get(`${apiEndpoint}/ai-feedback/export?type=${type}`, { responseType: 'blob' }).then(res => res.data)
  }

  // ==============================
  // Ultromics Import Methods
  // ==============================

  uploadUltromicsFile(file: File) {
    const formData = new FormData()
    formData.append('file0', file)
    const endpoint = `${apiEndpoint}/ultromics/upload`
    return axios.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  listUltromicsFiles() {
    const endpoint = `${apiEndpoint}/ultromics/files`
    return axios.get(endpoint)
  }

  deleteUltromicsFile(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/files/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  startUltromicsImport(files: string[]) {
    const endpoint = `${apiEndpoint}/ultromics/import`
    return axios.post(endpoint, { files })
  }

  checkUltromicsImportStatus() {
    const endpoint = `${apiEndpoint}/ultromics/import/status`
    // Cache-bust to prevent browser from returning stale "ongoing" responses
    return axios.get(endpoint, { params: { _t: Date.now() } })
  }

  killUltromicsImport(force: boolean = false) {
    const endpoint = `${apiEndpoint}/ultromics/import/kill`
    return axios.post(endpoint, { force })
  }

  listUltromicsReports() {
    const endpoint = `${apiEndpoint}/ultromics/import/reports`
    return axios.get(endpoint)
  }

  downloadUltromicsReportByName(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/import/reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteUltromicsReport(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/import/reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  listUltromicsProcessedFiles() {
    const endpoint = `${apiEndpoint}/ultromics/processed`
    return axios.get(endpoint)
  }

  downloadUltromicsProcessedFile(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/processed/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteUltromicsProcessedFile(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/processed/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  listUltromicsMeasurementReports() {
    const endpoint = `${apiEndpoint}/ultromics/measurement-reports`
    return axios.get(endpoint)
  }

  downloadUltromicsMeasurementReport(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/measurement-reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteUltromicsMeasurementReport(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/measurement-reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  listUltromicsEpicRawReports() {
    const endpoint = `${apiEndpoint}/ultromics/epic-raw-reports`
    return axios.get(endpoint)
  }

  downloadUltromicsEpicRawReport(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/epic-raw-reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteUltromicsEpicRawReport(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/epic-raw-reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  listFhirImportEpicRawReports() {
    const endpoint = `${apiEndpoint}/fhir-import/epic-raw-reports`
    return axios.get(endpoint)
  }

  downloadFhirImportEpicRawReport(filename: string) {
    const endpoint = `${apiEndpoint}/fhir-import/epic-raw-reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteFhirImportEpicRawReport(filename: string) {
    const endpoint = `${apiEndpoint}/fhir-import/epic-raw-reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  // Ultromics Azure Blob Sync Methods
  // ==============================

  getUltromicsAzureSyncConfig() {
    const endpoint = `${apiEndpoint}/ultromics/azure-sync/config`
    return axios.get(endpoint)
  }

  syncUltromicsFromAzure() {
    const endpoint = `${apiEndpoint}/ultromics/azure-sync`
    return axios.post(endpoint, {})
  }

  checkUltromicsAzureSyncStatus() {
    const endpoint = `${apiEndpoint}/ultromics/azure-sync/status`
    return axios.get(endpoint)
  }

  listUltromicsSyncReports() {
    const endpoint = `${apiEndpoint}/ultromics/azure-sync/reports`
    return axios.get(endpoint)
  }

  downloadUltromicsSyncReportByName(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/azure-sync/reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteUltromicsSyncReport(filename: string) {
    const endpoint = `${apiEndpoint}/ultromics/azure-sync/reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  // CA Diagnosis Import Methods
  // ==============================

  uploadCADiagnosisFile(file: File) {
    const formData = new FormData()
    formData.append('file0', file)
    const endpoint = `${apiEndpoint}/ca-diagnosis/upload`
    return axios.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  listCADiagnosisFiles() {
    const endpoint = `${apiEndpoint}/ca-diagnosis/files`
    return axios.get(endpoint)
  }

  deleteCADiagnosisFile(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/files/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  startCADiagnosisImport(files: string[]) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/import`
    return axios.post(endpoint, { files })
  }

  checkCADiagnosisImportStatus() {
    const endpoint = `${apiEndpoint}/ca-diagnosis/import/status`
    return axios.get(endpoint, { params: { _t: Date.now() } })
  }

  killCADiagnosisImport(force: boolean = false) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/import/kill`
    return axios.post(endpoint, { force })
  }

  listCADiagnosisReports() {
    const endpoint = `${apiEndpoint}/ca-diagnosis/import/reports`
    return axios.get(endpoint)
  }

  downloadCADiagnosisReportByName(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/import/reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteCADiagnosisReport(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/import/reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  listCADiagnosisProcessedFiles() {
    const endpoint = `${apiEndpoint}/ca-diagnosis/processed`
    return axios.get(endpoint)
  }

  downloadCADiagnosisProcessedFile(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/processed/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteCADiagnosisProcessedFile(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/processed/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  // cardio_consult proxy — see ~/atman/cardio_consult/docs/INTEGRATION.md
  // PHI gate: do not pass patient data in the question field pre-Phase-2.
  cardioConsultHealth() {
    return axios.get(`${apiEndpoint}/cardio-consult/health`)
  }
  cardioConsult(payload: { question: string; mode: 'consult' | 'lookup'; top_k?: number; label?: string }) {
    return axios.post(`${apiEndpoint}/cardio-consult`, payload, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      timeout: 125000, // service 95th ~60s; client cap matches doc §6 (120s)
    })
  }

  listCADiagnosisMeasurementReports() {
    const endpoint = `${apiEndpoint}/ca-diagnosis/measurement-reports`
    return axios.get(endpoint)
  }

  downloadCADiagnosisMeasurementReport(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/measurement-reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteCADiagnosisMeasurementReport(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/measurement-reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }

  listCADiagnosisEpicRawReports() {
    const endpoint = `${apiEndpoint}/ca-diagnosis/epic-raw-reports`
    return axios.get(endpoint)
  }

  downloadCADiagnosisEpicRawReport(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/epic-raw-reports/${encodeURIComponent(filename)}`
    return axios.get(endpoint, { responseType: 'blob' })
  }

  deleteCADiagnosisEpicRawReport(filename: string) {
    const endpoint = `${apiEndpoint}/ca-diagnosis/epic-raw-reports/${encodeURIComponent(filename)}`
    return axios.delete(endpoint)
  }
}
